import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/StatusBadge";
import { PolicyTypeBadge } from "@/components/PolicyTypeBadge";
import { PolicyPaymentsSection } from "@/components/PolicyPaymentsSection";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, Pencil, RefreshCw, PauseCircle, PlayCircle, Sparkles, History, ChevronDown, ChevronUp } from "lucide-react";
import { api, formatCurrency, formatDate, formatDateTime } from "@/lib/api";
import { typeLabel } from "@/lib/policyConstants";

// 🆕 Feb 2026 — Cronologia Classi di Merito del cliente (collapsible)
function MeritClassHistorySection({ clientId, currentPolicyId }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || items !== null) return;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const r = await api.get(`/clients/${clientId}/merit-class-history`);
        if (!cancelled) setItems(Array.isArray(r.data?.items) ? r.data.items : []);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || "Errore caricamento cronologia");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, clientId, items]);

  if (!clientId) return null;

  return (
    <div data-testid="policy-detail-merit-history" className="rounded-md border border-slate-200 bg-white p-3">
      <button
        type="button"
        data-testid="merit-history-toggle"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between text-left"
      >
        <div className="flex items-center gap-2 font-semibold text-slate-800">
          <History className="h-4 w-4 text-indigo-600" />
          Cronologia Classi di Merito
          {items !== null && (
            <span className="text-[10px] font-bold uppercase bg-slate-100 text-slate-700 px-2 py-0.5 rounded-full">
              {items.length}
            </span>
          )}
        </div>
        {open ? <ChevronUp className="h-4 w-4 text-slate-500" /> : <ChevronDown className="h-4 w-4 text-slate-500" />}
      </button>
      {open && (
        <div className="mt-3 space-y-2">
          {loading && <div className="text-xs text-slate-500">Caricamento…</div>}
          {error && <div className="text-xs text-rose-600">⚠️ {error}</div>}
          {!loading && !error && items !== null && items.length === 0 && (
            <div className="text-xs text-slate-500 italic">Nessun cambio classe registrato.</div>
          )}
          {!loading && !error && items && items.length > 0 && (
            <ol className="relative border-l-2 border-indigo-100 ml-2 pl-4 space-y-2">
              {items.map((it) => {
                const isCurrent = it.policy_id === currentPolicyId;
                const arrow = it.old_class ? `${it.old_class} → ${it.new_class}` : `${it.new_class} (inizio)`;
                return (
                  <li key={it.id} className="relative" data-testid={`merit-history-item-${it.id}`}>
                    <span className={`absolute -left-[22px] top-1 h-3 w-3 rounded-full border-2 ${isCurrent ? "bg-emerald-500 border-emerald-600" : "bg-white border-indigo-400"}`} />
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <span className="font-mono text-sm font-bold text-indigo-800">{arrow}</span>
                      {it.year && <span className="text-[10px] bg-slate-100 text-slate-700 px-2 py-0.5 rounded uppercase font-bold">{it.year}</span>}
                      {isCurrent && <span className="text-[10px] bg-emerald-600 text-white px-2 py-0.5 rounded uppercase font-bold">Questa polizza</span>}
                    </div>
                    <div className="text-[11px] text-slate-500 mt-0.5">
                      {formatDateTime(it.changed_at) || "—"}
                      {it.changed_by?.name ? <> · <span className="text-slate-600">{it.changed_by.name}</span></> : null}
                      {it.source ? <> · <span className="italic">{it.source.replace(/_/g, " ")}</span></> : null}
                    </div>
                  </li>
                );
              })}
            </ol>
          )}
        </div>
      )}
    </div>
  );
}

// 🆕 Feb 2026 — Alert Bersani (D.L. 7/2007): quando il passaggio di proprietà
// è avvenuto entro gli ultimi 30 giorni, l'agente può proporre l'ereditarietà
// della classe di merito. Ritorna null se non applicabile.
function bersaniInfo(purchaseDate, policyType) {
  if (!purchaseDate) return null;
  const isVehiclePolicy = ["auto", "moto", "autocarro", "ciclomotore", "veicolo"].some((t) =>
    (policyType || "").toLowerCase().includes(t)
  );
  if (!isVehiclePolicy) return null;
  const d = new Date(purchaseDate);
  if (isNaN(d.getTime())) return null;
  const today = new Date();
  const diffMs = today.setHours(0, 0, 0, 0) - new Date(d).setHours(0, 0, 0, 0);
  const daysAgo = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (daysAgo < 0 || daysAgo > 30) return null;
  return { daysAgo, expiresIn: 30 - daysAgo };
}

function DetailRow({ label, value, mono = false }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wide text-slate-500">{label}</div>
      <div className={`font-medium text-slate-800 ${mono ? "font-mono uppercase text-xs" : ""}`}>{value || "—"}</div>
    </div>
  );
}

/**
 * Read-only "Vedi" dialog for a policy.
 * Props:
 *  - policy: the policy object (or null/undefined to hide)
 *  - brokers: list of brokers for broker_name lookup
 *  - canWrite: boolean → show "Modifica" button
 *  - onClose: () => void
 *  - onEdit: (policy) => void
 *  - onRenew: (policy) => void  (apre il dialog di rinnovo)
 *  - onSuspend: (policy) => void  (apre il dialog di sospensione)
 *  - onReactivate: (policy) => void  (apre il dialog di riattivazione)
 *  - onExtendSuspension: (policy) => void  (apre il dialog di proroga sospensione)
 */
export function PolicyDetailDialog({ policy, brokers, canWrite, onClose, onEdit, onRenew, onSuspend, onReactivate, onExtendSuspension, onUndoReactivation }) {
  const navigate = useNavigate();
  const brokerName = policy?.broker_id
    ? (brokers.find((b) => b.id === policy.broker_id)?.name || "—")
    : "—";

  return (
    <Dialog open={!!policy} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="policy-detail-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Scheda polizza · {policy?.policy_number}
            {policy && <StatusBadge status={policy.status} />}
          </DialogTitle>
        </DialogHeader>
        {policy && (
          <div className="space-y-4 text-sm">
            {/* Banner: questa polizza deriva da una precedente (Rinnovo / Sostituzione / Offerta) */}
            {policy.renewed_from_policy_id && (() => {
              // 🆕 Feb 2026 — Banner dinamico secondo link_type
              const lt = policy.link_type;
              const cfg = lt === "replacement"
                ? {
                    title: "Polizza sostituita",
                    body: <>Questa polizza è la <strong>sostituzione</strong> di una precedente già archiviata.</>,
                    icon: "🔄",
                    box: "border-blue-300 bg-gradient-to-r from-blue-50 to-sky-50",
                    text: "text-blue-800",
                    sub: "text-blue-700",
                  }
                : lt === "from_quote_offer"
                  ? {
                      title: "Polizza generata da offerta",
                      body: <>Questa polizza è stata <strong>generata da un&apos;offerta finale</strong> accettata.</>,
                      icon: "📄",
                      box: "border-violet-300 bg-gradient-to-r from-violet-50 to-purple-50",
                      text: "text-violet-800",
                      sub: "text-violet-700",
                    }
                  : {
                      title: "Polizza rinnovata",
                      body: <>Questa polizza è il <strong>rinnovo</strong> di una posizione precedente già archiviata.</>,
                      icon: null,
                      box: "border-amber-300 bg-gradient-to-r from-amber-50 to-yellow-50",
                      text: "text-amber-800",
                      sub: "text-amber-700",
                    };
              return (
                <div data-testid="policy-detail-renewed-from" className={`rounded-md border-2 p-3 ${cfg.box}`}>
                  <div className={`flex items-center gap-2 font-bold ${cfg.text}`}>
                    {cfg.icon ? <span className="text-lg leading-none">{cfg.icon}</span> : <RefreshCw className="h-4 w-4" />}
                    {cfg.title}
                  </div>
                  <p className={`text-xs mt-1 ${cfg.sub}`}>
                    {cfg.body}{" "}
                    ID polizza originale: <span className="font-mono">{policy.renewed_from_policy_id?.slice(0, 8)}…</span>
                  </p>
                </div>
              );
            })()}
            {/* Banner: questa polizza è STATA rinnovata (è ora la "vecchia") */}
            {policy.renewed_to_policy_id && (
              <div data-testid="policy-detail-renewed-to" className="rounded-md border-2 border-slate-300 bg-slate-50 p-3">
                <div className="flex items-center gap-2 text-slate-700 font-bold">
                  🗃️ Polizza archiviata (sostituita da rinnovo)
                </div>
                <p className="text-xs text-slate-600 mt-1">
                  Questa polizza è stata <strong>rinnovata</strong> il {policy.renewed_at ? formatDate(policy.renewed_at) : "—"} e sostituita da una nuova.
                  ID nuovo rinnovo: <span className="font-mono">{policy.renewed_to_policy_id?.slice(0, 8)}…</span>
                </p>
              </div>
            )}
            {/* 🆕 Feb 2026 — Alert Bersani (D.L. 7/2007): passaggio di proprietà recente */}
            {(() => {
              const b = bersaniInfo(policy.vehicle_purchase_date, policy.type);
              if (!b) return null;
              const isCU = String(policy.merit_class || "").toUpperCase().startsWith("CU");
              return (
                <div data-testid="policy-detail-bersani-alert" className="rounded-md border-2 border-emerald-400 bg-gradient-to-r from-emerald-50 to-teal-50 p-3 shadow-sm">
                  <div className="flex items-start gap-2">
                    <Sparkles className="h-5 w-5 text-emerald-600 mt-0.5 shrink-0" />
                    <div className="flex-1">
                      <div className="font-bold text-emerald-800 flex items-center gap-2 flex-wrap">
                        Bersani disponibile
                        <span className="text-[10px] font-bold uppercase tracking-wider bg-emerald-600 text-white px-2 py-0.5 rounded-full">
                          {b.daysAgo === 0 ? "OGGI" : `${b.daysAgo}g fa`}
                        </span>
                        {isCU && (
                          <span data-testid="bersani-cu-badge" className="text-[10px] font-bold uppercase tracking-wider bg-indigo-600 text-white px-2 py-0.5 rounded-full">
                            ✓ CU {policy.merit_class} applicata
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-emerald-800 mt-1">
                        Il passaggio di proprietà del veicolo è avvenuto il{" "}
                        <strong>{formatDate(policy.vehicle_purchase_date)}</strong> ({b.daysAgo} giorni fa).
                        Il cliente può ereditare la classe di merito da un veicolo di proprietà o di un familiare convivente (D.L. 7/2007 “Bersani”).
                      </p>
                      <p className="text-[11px] text-emerald-700 mt-1">
                        {isCU ? (
                          <>✅ La polizza usa già la <strong>Classe Universale {policy.merit_class}</strong> (Bersani).</>
                        ) : (
                          <>⏳ Restano <strong>{b.expiresIn} giorni</strong> nella finestra dei 30 giorni per applicare la classe agevolata.</>
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })()}
            {/* 🆕 Feb 2026 — Cronologia Classi di Merito cliente */}
            {policy.client_id && (
              <MeritClassHistorySection clientId={policy.client_id} currentPolicyId={policy.id} />
            )}
            {policy.status === "sospesa" && (
              <div data-testid="policy-detail-suspension" className="rounded-md border border-amber-300 bg-amber-50 p-3">
                <div className="flex items-center gap-2 text-amber-800 font-semibold">
                  <AlertTriangle className="h-4 w-4" /> Polizza sospesa
                </div>
                <div className="mt-2 grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <div className="text-[11px] uppercase tracking-wide text-amber-700">Data sospensione</div>
                    <div className="font-medium tabular-nums">{formatDate(policy.suspension_date) || "—"}</div>
                  </div>
                  <div>
                    <div className="text-[11px] uppercase tracking-wide text-amber-700">Data massima di riattivazione</div>
                    <div className="font-semibold text-amber-900 tabular-nums">{formatDate(policy.reactivation_deadline) || "—"}</div>
                  </div>
                </div>
                <p className="text-[11px] text-amber-700 mt-2">
                  Trascorsa questa data, il contratto annuale viene perduto. Il cliente riceve un avviso 15 giorni prima.
                </p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <DetailRow label="Cliente" value={policy.client_name} />
              <DetailRow label="Codice Fiscale" value={policy.client_fiscal_code} mono />
              <DetailRow label="N° Polizza" value={policy.policy_number} />
              <div>
                <div className="text-[11px] uppercase tracking-wide text-slate-500">Tipo</div>
                <div className="mt-1"><PolicyTypeBadge code={policy.type} size="sm" /></div>
              </div>
              <DetailRow label="Compagnia" value={policy.company} />
              <DetailRow label="Agenzia / Broker" value={brokerName} />
              <DetailRow label="Decorrenza" value={formatDate(policy.start_date)} />
              <DetailRow label="Scadenza" value={formatDate(policy.end_date)} />
              <DetailRow label="Frazionamento" value={policy.frazionamento} />
              <DetailRow label="Premio" value={formatCurrency(policy.premium)} />
              <DetailRow label="Provvigione %" value={policy.commission_percent ? `${policy.commission_percent}%` : "—"} />
              <DetailRow label="Provvigione €" value={policy.commission_amount ? formatCurrency(policy.commission_amount) : "—"} />
              {policy.commission_cost ? <DetailRow label="Costo commissioni" value={formatCurrency(policy.commission_cost)} /> : null}
              {policy.vehicle_plate && <DetailRow label="Veicolo" value={`${policy.vehicle_plate} · ${policy.vehicle_brand || ""} ${policy.vehicle_model || ""}`.trim()} />}
              {policy.vehicle_first_registration && <DetailRow label="1ª immatricolazione" value={formatDate(policy.vehicle_first_registration)} />}
              {policy.vehicle_purchase_date && <DetailRow label="Data acquisto veicolo" value={formatDate(policy.vehicle_purchase_date)} />}
              {policy.notes && <div className="col-span-2"><DetailRow label="Note" value={policy.notes} /></div>}
              <DetailRow label="Avviso cliente scadenza" value={policy.notify_expiry_client === false ? "Disattivato" : "Attivo"} />
              <div className="col-span-2 mt-2 pt-2 border-t border-slate-200 grid grid-cols-2 gap-4 text-xs">
                <div data-testid="policy-created-at">
                  <div className="text-[11px] uppercase tracking-wide text-slate-500">Inserito il</div>
                  <div className="font-medium text-slate-700 tabular-nums">
                    {policy.created_at ? formatDateTime(policy.created_at) : "—"}
                    {policy.created_by?.name && <span className="text-slate-500"> · da {policy.created_by.name}</span>}
                  </div>
                </div>
                <div data-testid="policy-updated-at">
                  <div className="text-[11px] uppercase tracking-wide text-slate-500">Ultima modifica</div>
                  <div className="font-medium text-slate-700 tabular-nums">
                    {policy.updated_at ? formatDateTime(policy.updated_at) : "—"}
                    {policy.updated_by?.name && <span className="text-slate-500"> · da {policy.updated_by.name}</span>}
                  </div>
                </div>
              </div>
              {/* 🆕 Sezione stato Sospensione corrente + Storico sospensioni/riattivazioni/proroghe */}
              {(policy.status === "sospesa" || (policy.suspension_history && policy.suspension_history.length > 0)) && (
                <div className="col-span-2 mt-3 pt-3 border-t border-slate-200" data-testid="policy-suspension-history">
                  <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold mb-2">
                    {policy.status === "sospesa" ? "⏸️ Stato sospensione + Storico" : "📜 Storico sospensioni"}
                  </div>
                  {policy.status === "sospesa" && (
                    <div className="mb-2 p-2.5 rounded bg-orange-50 border border-orange-200 text-xs space-y-1">
                      <div><b>Sospesa dal:</b> {formatDate(policy.suspension_start_date)}</div>
                      {policy.reactivation_deadline && <div><b>Termine max riattivazione:</b> {formatDate(policy.reactivation_deadline)}</div>}
                      {policy.suspension_max_months && <div><b>Mesi max (compagnia):</b> {policy.suspension_max_months}</div>}
                      {policy.suspension_reason && <div><b>Motivo:</b> {policy.suspension_reason}</div>}
                    </div>
                  )}
                  {policy.suspension_history && policy.suspension_history.length > 0 && (
                    <ul className="space-y-1.5 text-xs">
                      {policy.suspension_history.map((h, i) => (
                        <li key={i} className="p-2 rounded border border-slate-200 bg-slate-50">
                          {h.kind === "extension" ? (
                            <>🕐 <b>Proroga</b>: {formatDate(h.previous_deadline)} → {formatDate(h.new_deadline)}
                              {h.reason && <> · <span className="text-slate-600">{h.reason}</span></>}
                              <span className="text-slate-400 text-[10px] ml-2">({formatDate(h.logged_at)})</span>
                            </>
                          ) : (
                            <>▶️ <b>Sospesa</b> {formatDate(h.suspension_start_date)} → <b>Riattivata</b> {formatDate(h.reactivation_date)}
                              {" "}· <b>{h.days_suspended}gg</b> di fermo
                              {" "}· scadenza: {formatDate(h.old_end_date)} → <b>{formatDate(h.new_end_date)}</b>
                              {h.reason && <> · <span className="text-slate-600">{h.reason}</span></>}
                            </>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
              <PolicyPaymentsSection
                policyId={policy.id}
                onOpenReceipt={(tx) => { onClose(); navigate(`/admin/pagamenti/ricevuta/${tx.id}`); }}
              />
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="close-policy-detail">Chiudi</Button>
          {policy && canWrite && onSuspend && policy.status === "attiva" && (
            <Button
              data-testid="suspend-from-detail"
              className="bg-orange-500 hover:bg-orange-600 text-white"
              onClick={() => onSuspend(policy)}
              title="Sospendi temporaneamente questa polizza"
            >
              <PauseCircle className="h-4 w-4 mr-2" /> Sospendi
            </Button>
          )}
          {policy && canWrite && onReactivate && policy.status === "sospesa" && (
            <Button
              data-testid="reactivate-from-detail"
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              onClick={() => onReactivate(policy)}
              title="Riattiva la polizza — la nuova scadenza sarà calcolata in base ai giorni di fermo"
            >
              <PlayCircle className="h-4 w-4 mr-2" /> Riattiva
            </Button>
          )}
          {policy && canWrite && onExtendSuspension && policy.status === "sospesa" && (
            <Button
              data-testid="extend-from-detail"
              className="bg-orange-400 hover:bg-orange-500 text-white"
              onClick={() => onExtendSuspension(policy)}
              title="Proroga il termine massimo di riattivazione (regola dei 10 giorni)"
            >
              🕐 Proroga
            </Button>
          )}
          {policy && canWrite && onRenew && policy.status !== "rinnovata" && policy.status !== "sospesa" && (
            <Button
              data-testid="renew-from-detail"
              className="bg-amber-500 hover:bg-amber-600 text-white"
              onClick={() => onRenew(policy)}
            >
              <RefreshCw className="h-4 w-4 mr-2" /> Rinnova polizza
            </Button>
          )}
          {policy && canWrite && onUndoReactivation && policy.was_reactivated && policy.status === "attiva" && policy.reactivated_from_policy_id && (
            <Button
              data-testid="undo-reactivation-from-detail"
              variant="outline"
              className="border-rose-300 text-rose-700 hover:bg-rose-50"
              onClick={() => onUndoReactivation(policy)}
              title="Annulla la riattivazione: elimina questa polizza duplicata e ripristina l'originale in stato sospesa"
            >
              🗑️ Annulla riattivazione
            </Button>
          )}
          {policy && canWrite && (
            <Button data-testid="edit-from-detail" className="bg-[#0F172A] hover:bg-[#1E293B]" onClick={() => onEdit(policy)}>
              <Pencil className="h-4 w-4 mr-2" /> Modifica
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
